import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { TokenPayload, UserInfo } from '../models/token-payload';

const SESSION_TOKEN_KEY = 'allo_access_token';
const FDCN_ID = 'fdcn_id';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  constructor(private auth: AuthService) {}

  addToken(token: string): void {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    sessionStorage.setItem(FDCN_ID, this.getFdcnId()?.toString() || '');
  }

  getToken(): string | null {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  }

  clearToken(): void {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    sessionStorage.removeItem(FDCN_ID);
  }

  isTokenExpired(token: string): boolean {
    try {
      const payload = this.getDecodedToken(token);
      return (Math.floor((new Date()).getTime() / 1000)) >= payload.exp;
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true; // Assume expired if we can't decode
    }
  }

  getDecodedToken(token: string): TokenPayload {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload as TokenPayload;
    } catch (error) {
      console.error('Error decoding token:', error);
      throw new Error('Invalid token format');
    }
  }

  // Get a more user-friendly representation of the token
  getUserInfo(token?: string): UserInfo | null {
    try {
      const tokenToUse = token || this.getToken();
      if (!tokenToUse) {
        return null;
      }

      const payload = this.getDecodedToken(tokenToUse);
      
      return {
        username: payload['http://tradestation.com/username'],
        fdcnId: Number(payload['http://tradestation.com/fdcn_id']),
        onyxId: payload['http://tradestation.com/onyx_id'],
        tempCredentials: payload['http://tradestation.com/temp_credentials'],
        issuer: payload.iss,
        subject: payload.sub,
        audience: payload.aud,
        issuedAt: new Date(payload.iat * 1000),
        expiresAt: new Date(payload.exp * 1000),
        scope: payload.scope,
        authorizedParty: payload.azp
      };
    } catch (error) {
      console.error('Error getting user info from token:', error);
      return null;
    }
  }

  // Helper methods for common token properties
  getUsername(): string | null {
    const userInfo = this.getUserInfo();
    return userInfo?.username || null;
  }

  getFdcnId(): number | null {
    const userInfo = this.getUserInfo();
    return userInfo?.fdcnId || null;
  }

  getOnyxId(): string | null {
    const userInfo = this.getUserInfo();
    return userInfo?.onyxId || null;
  }

  hasTempCredentials(): boolean {
    const userInfo = this.getUserInfo();
    return userInfo?.tempCredentials || false;
  }

  getTokenExpirationDate(): Date | null {
    const userInfo = this.getUserInfo();
    return userInfo?.expiresAt || null;
  }

  // Check if token is valid (exists and not expired)
  isTokenValid(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    return !this.isTokenExpired(token);
  }
}
